import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, MapPin, Sparkles } from "lucide-react";
import { AUTHORS } from "@/data/authors";
import { Card, CardContent } from "@/components/ui/card";
import SEO from "@/components/SEO";
import { SITE_URL as BASE } from "@/lib/site";

export default function AuthorProfile() {
  const { slug } = useParams();
  const a = AUTHORS[slug];

  if (!a) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-white/60">
        Author not found. <Link to="/about" className="text-gold underline">Back to About</Link>.
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": a.name,
    "jobTitle": a.role,
    "image": `${BASE}${a.photo}`,
    "description": a.bio,
    "url": `${BASE}/authors/${a.slug}`,
    "affiliation": {
      "@type": "NGO",
      "name": "International Organized Crime Research Institute",
      "url": BASE,
    },
    "knowsAbout": a.tags,
  };

  const isPrimary = a.slug === "shriya-das";
  const gridCols = isPrimary ? "md:grid-cols-[320px,1fr]" : "md:grid-cols-[220px,1fr]";
  const nameSize = isPrimary ? "text-5xl md:text-6xl" : "text-4xl md:text-5xl";

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-12" data-testid={`author-page-${a.slug}`}>
      <SEO
        title={`${a.name} · ${a.role}`}
        description={a.bio.slice(0, 200)}
        path={`/authors/${a.slug}`}
        image={a.photo}
        type="profile"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Link to="/about" data-testid="back-to-about" className="inline-flex items-center gap-2 text-white/60 hover:text-gold text-sm">
        <ArrowLeft className="h-4 w-4" /> Back to About
      </Link>

      <div className={`mt-8 grid ${gridCols} gap-10 items-start`}>
        <div>
          <div className="relative rounded-3xl overflow-hidden border border-gold/30 shadow-[0_20px_60px_-20px_rgba(212,175,55,0.4)]" data-testid="author-photo">
            <img src={a.photo} alt={a.name} className="w-full h-auto block" />
            <div className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-white/5 rounded-3xl" />
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            {a.tags.map((t) => (
              <span key={t} className="text-[10px] font-mono tracking-widest text-white/60 border border-white/10 rounded-full px-2.5 py-1">
                {t.toUpperCase()}
              </span>
            ))}
          </div>
        </div>

        <div>
          <div className="text-gold font-mono text-xs tracking-[0.35em]">{a.role.toUpperCase()}</div>
          <h1 className={`font-display ${nameSize} mt-2 text-white leading-tight`}>{a.name}</h1>

          <div className="divider-gold my-8" />

          <Card className="bg-obsidian-soft border-white/5 rounded-2xl">
            <CardContent className="p-8">
              <div className="text-gold font-mono text-xs tracking-widest flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5" /> BIOGRAPHY
              </div>
              <p className="mt-4 text-white/85 text-lg leading-relaxed">{a.bio}</p>
            </CardContent>
          </Card>

          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            <div className="rounded-2xl border border-white/5 bg-obsidian-soft p-5">
              <div className="text-[10px] font-mono tracking-widest text-gold">AFFILIATION</div>
              <div className="font-display text-lg text-white mt-2">IOCRI</div>
              <div className="text-white/50 text-xs">International Organized Crime Research Institute</div>
            </div>
            <div className="rounded-2xl border border-white/5 bg-obsidian-soft p-5">
              <div className="text-[10px] font-mono tracking-widest text-gold">FOCUS AREAS</div>
              <div className="font-display text-lg text-white mt-2">Transnational Crime · Law · Global Affairs</div>
              <div className="text-white/50 text-xs">Research and public education</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
